import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import MapboxMap, { Marker, Source, Layer, NavigationControl, FullscreenControl } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import circle from "@turf/circle";
import { ArrowLeft } from 'lucide-react';
import userPreferencesService from "../services/userPreferencesService";
import { useBranding } from "../contexts/BrandingContext";
import { MAP_STYLES, applyMapboxStyleExtras } from "../utils/mapboxStyle";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export default function MapLanding() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [tolerance, setTolerance] = useState(null);
  const [inputTolerance, setInputTolerance] = useState("");
  const [loading, setLoading] = useState(false);
  const [movingPin, setMovingPin] = useState(false);
  const [project, setProject] = useState(null);
  const [mapType, setMapType] = useState('satellite');
  const mapRef = useRef(null);

  const { tokens } = useBranding();

  // fetch coordinates & saved tolerance
  useEffect(() => {
    const fetchProject = async () => {
      const snap = await getDoc(doc(db, "projects", projectId));
      if (snap.exists()) {
        const data = snap.data();
        setProject({ id: snap.id, ...data });
        setLat(data.latitude);
        setLon(data.longitude);
        if (data.gpsToleranceMeters != null) {
          setTolerance(data.gpsToleranceMeters);
          setInputTolerance(String(data.gpsToleranceMeters));
        }
      }
    };
    fetchProject();
  }, [projectId]);

  // Load map type preference
  useEffect(() => {
    const loadMapType = async () => {
      try {
        const type = await userPreferencesService.loadMapType();
        setMapType(type || 'satellite');
        console.log('🗺️ Map type preference loaded for MapLanding:', type);
      } catch (error) {
        console.error('Error loading map type preference:', error);
      }
    };
    loadMapType();
  }, []);

  // save new tolerance and update displayed circle
  const saveTolerance = async () => {
    if (project?.status === 'closed') {
      alert('This project is closed. You cannot modify the map settings.');
      return;
    }
    setLoading(true);
    const newTol = parseInt(inputTolerance, 10) || 0;
    await updateDoc(doc(db, "projects", projectId), {
      gpsToleranceMeters: newTol,
    });
    setTolerance(newTol);
    setLoading(false);
  };

  // handle marker drag to update project coordinates
  const handleMarkerDragEnd = async (event) => {
    if (project?.status === 'closed') {
      alert('This project is closed. You cannot modify the map location.');
      return;
    }
    const newLat = event.lngLat.lat;
    const newLng = event.lngLat.lng;

    setMovingPin(true);
    try {
      await updateDoc(doc(db, "projects", projectId), {
        latitude: newLat,
        longitude: newLng,
      });

      setLat(newLat);
      setLon(newLng);
    } catch (error) {
      console.error('Error updating project coordinates:', error);
      setLat(lat);
      setLon(lon);
    } finally {
      setMovingPin(false);
    }
  };

  // GPS tolerance radius as a GeoJSON circle
  const toleranceCircle = useMemo(() => {
    if (lat == null || lon == null || tolerance == null) return null;
    return circle([lon, lat], tolerance / 1000, { steps: 64, units: 'kilometers' });
  }, [lat, lon, tolerance]);

  // 3D terrain + brand theming - re-applied on every style load, not just the first,
  // since mapType can flip (satellite <-> roadmap) after mount once the saved
  // preference loads, which swaps the underlying style and wipes custom sources/config.
  // hideLabels: false - satellite mode here wants labels visible for way-finding
  // while placing a pin (unlike the compact dashboard preview).
  const onMapLoad = useCallback((event) => {
    const map = event.target;
    mapRef.current = map;
    applyMapboxStyleExtras(map, { hideLabels: false });
    map.on('style.load', () => applyMapboxStyleExtras(map, { hideLabels: false }));
  }, []);

  if (!MAPBOX_TOKEN || lat == null || lon == null) {
    return <p style={{ padding: 24, textAlign: "center" }}>Loading map…</p>;
  }

  return (
    <div style={{ padding: "24px", width: "100%" }}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Back Button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = '#6b7280';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
        Project map & radius
      </h2>
      <p style={{ fontSize: 14, color: 'var(--cc-text-muted)', marginBottom: 16 }}>
        Drag the pin to update the project location. The sign-in radius will automatically adjust to the new position.
      </p>

      {/* Closed project notice */}
      {project?.status === 'closed' && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            color: '#92400e',
            fontSize: 14,
            fontWeight: 500
          }}>
            This project is closed and read-only. You can view the map but cannot modify the location or radius.
          </p>
        </div>
      )}

      {/* Map container with smart responsive sizing */}
      <div style={{
        maxWidth: "min(1200px, 90vw)",
        width: "100%",
        margin: "0 auto",
        marginBottom: "24px"
      }}>
        <div style={{
          position: "relative",
          width: "100%",
          height: "clamp(400px, 50vh, 600px)",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          border: "1px solid #e5e7eb",
          backgroundColor: "var(--branding-app-surface-panel)"
        }}>
          <MapboxMap
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={{ latitude: lat, longitude: lon, zoom: 17 }}
            mapStyle={MAP_STYLES[mapType] || MAP_STYLES.satellite}
            style={{ width: "100%", height: "100%" }}
            onLoad={onMapLoad}
          >
            <NavigationControl position="top-right" />
            <FullscreenControl position="top-right" />

            <Marker
              longitude={lon}
              latitude={lat}
              draggable={project?.status !== 'closed'}
              onDragEnd={handleMarkerDragEnd}
              color="#dc2626"
            />

            {toleranceCircle && (
              <Source id="gps-tolerance" type="geojson" data={toleranceCircle}>
                <Layer
                  id="gps-tolerance-fill"
                  type="fill"
                  paint={{ 'fill-color': '#60a5fa', 'fill-opacity': 0.2 }}
                />
                <Layer
                  id="gps-tolerance-outline"
                  type="line"
                  paint={{ 'line-color': tokens.appPrimary, 'line-width': 2, 'line-opacity': 0.8 }}
                />
              </Source>
            )}
          </MapboxMap>
        </div>
      </div>

      <div style={{ marginTop: 24, maxWidth: 320 }}>
        <label style={{ display: "block", fontWeight: 500, marginBottom: 8 }}>
          Sign-in radius (meters)
        </label>
        <input
          type="number"
          min={10}
          max={1000}
          value={inputTolerance}
          onChange={(e) => setInputTolerance(e.target.value)}
          placeholder="Enter radius…"
          disabled={project?.status === 'closed'}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            marginBottom: 16,
            backgroundColor: project?.status === 'closed' ? '#f9fafb' : '#fff',
            color: project?.status === 'closed' ? '#9ca3af' : '#000',
            cursor: project?.status === 'closed' ? 'not-allowed' : 'text'
          }}
        />
        <button
          onClick={saveTolerance}
          disabled={loading || movingPin || project?.status === 'closed'}
          style={{
            background: project?.status === 'closed' ? '#cbd5e1' : 'var(--branding-app-primary)',
            color: 'var(--branding-app-primary-fg)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '500',
            cursor: project?.status === 'closed' ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            opacity: project?.status === 'closed' ? 0.6 : 1
          }}
        >
          {loading ? "Saving…" : "Set range"}
        </button>

        {movingPin && (
          <div style={{
            marginTop: 12,
            padding: '8px 12px',
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#0369a1',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              border: '2px solid #0ea5e9',
              borderTopColor: 'transparent',
              animation: 'spin 1s linear infinite'
            }} />
            Updating project location...
          </div>
        )}
      </div>
    </div>
  );
}
